import { daGiorno, giornoItaliano } from './giorni.js';
import { locale, t } from './i18n/lingua.js';

/**
 * Come si scrivono date e importi.
 *
 * Un posto solo, perché la stessa data compare in elenco, nella scheda e
 * nell'esportazione: se ognuno la formatta a modo suo, la stessa spedizione
 * sembra due spedizioni diverse.
 *
 * E seguono la lingua: chi legge in inglese vede `8/8/2026` e `$1,543.20`,
 * chi legge in italiano `08/08/2026` e `1.543,20 $`. Una data scritta
 * all'italiana a un collega di Edison si legge al contrario — il 4/8 diventa
 * il 4 agosto invece dell'8 aprile — e sulle date di consegna è un guaio.
 */

/**
 * Data breve all'italiana; i vuoti diventano un trattino, non "Invalid Date".
 *
 * Un giorno semplice («2026-10-24», le date degli ordini) è un giorno e non
 * un istante: letto come istante sarebbe la mezzanotte di Greenwich, che a
 * Edison è ancora il giorno prima.
 */
export function data(valore: string | null | undefined): string {
  if (!valore) return '—';
  const d = daGiorno(valore) ?? new Date(valore);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(locale());
}

/**
 * Una data che a database è un istante ma vuol dire un giorno: la chiusura
 * prevista, la validità di un listino o di una proposta. Si legge il giorno
 * italiano, quello in cui è stata scritta, e lo si scrive come un giorno.
 */
export function giorno(valore: string | null | undefined): string {
  return data(giornoItaliano(valore));
}

/** Data e ora: serve nello storico, dove due modifiche possono essere dello stesso giorno. */
export function dataOra(valore: string | null | undefined): string {
  if (!valore) return '—';
  const d = new Date(valore);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString(locale())} ${d.toLocaleTimeString(locale(), {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/**
 * Importo con la valuta dell'ordine. Gli importi arrivano come stringhe —
 * `numeric` di Postgres — apposta: passarli per un numero JavaScript
 * arrotonderebbe i centesimi su cifre grandi.
 */
export function importo(valore: string | number | null | undefined, valuta?: string | null): string {
  if (valore === null || valore === undefined || valore === '') return '—';
  const n = typeof valore === 'number' ? valore : Number(valore);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString(locale(), {
    style: valuta ? 'currency' : 'decimal',
    currency: valuta ?? undefined,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Quantità: interi senza decimali inutili, decimali quando ci sono davvero. */
export function quantita(valore: number | null | undefined): string {
  if (valore === null || valore === undefined) return '—';
  return valore.toLocaleString(locale(), { maximumFractionDigits: 3 });
}

const PAGAMENTO: Record<string, { testo: string; classe: string }> = {
  unpaid: { testo: t('da incassare'), classe: 'badge-warn' },
  partial: { testo: t('parziale'), classe: 'badge-warn' },
  paid: { testo: t('incassato'), classe: 'badge-ok' },
};

/**
 * Come si mostra lo stato del pagamento. Se un giorno il database avesse un
 * valore nuovo, si vede quello com'è invece di lasciare la cella vuota.
 */
export function pastigliaPagamento(stato: string): { testo: string; classe: string } {
  const voce = PAGAMENTO[stato];
  return voce ? { ...voce, testo: t(voce.testo) } : { testo: stato, classe: '' };
}

/**
 * Un numero da leggere: 30.622 in italiano, 30,622 in inglese.
 *
 * Sta qui e non sparso nelle pagine perché `toLocaleString('it-IT')` scritto
 * a mano in venti posti è il modo sicuro per dimenticarne tre.
 */
export function numero(valore: number | null | undefined): string {
  if (valore === null || valore === undefined) return '—';
  return valore.toLocaleString(locale());
}
